import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  YardInspection,
  UpdateYardInspectionRegistrationParams,
} from "../../../store/oasis/yardInspectionsStore";
import {
  deriveInspectionStatus,
  inspectionStatusClasses,
  inspectionStatusIcon,
  inspectionStatusLabel,
} from "../../../store/oasis/yardInspectionsStore";
import {
  tagSurveyComplete,
  tagSurveyFrom,
  tagSurveyPayload,
  untaggedRegistrationComplete,
  TAG_POSITION_OPTIONS,
  TAG_PRESENCE_OPTIONS,
  type TagSurvey,
  type UntaggedStep,
} from "../../../store/oasis/specialInspection";
import { fetchYardAccessEvent } from "../../../store/oasis/hikvisionAccessStore";
import type { TruckYardVehicle } from "../../../store/truckYardStore";
import { useGroupsStore } from "../../../store/admin/groupsStore";
import { useAllGroups } from "../../../features/catalogs/queries";
import ManualRegistrationFields from "../../patios-maniobras/components/ManualRegistrationFields";
import {
  inspectionRegistrationPayload,
  isUnitMissing,
  registrationFormFromInspection,
  validateManualRegistration,
  type ManualRegistrationErrors,
  type ManualRegistrationFormData,
} from "../../patios-maniobras/components/manualRegistration";
import SpecialTagBadge from "../../../components/inspections/SpecialTagBadge";
import { brand } from "../../../config/theme";
import { modalYardName } from "../inspectionTimes";

/**
 * "1 Registro manual → 2 Inspección".
 *
 * Los pasos son botones porque se navega con ellos: al 1 se vuelve siempre a
 * corregir, y al 2 sólo con el registro hecho (`canOpenStep2`). El paso
 * bloqueado va `disabled` y no escondido, para que se vea que hay un segundo
 * paso antes de terminar el primero.
 */
export function UntaggedStepIndicator({
  current,
  canOpenStep2,
  onSelect,
  themeClasses,
  isDarkMode,
}: {
  current: UntaggedStep;
  canOpenStep2: boolean;
  onSelect: (step: UntaggedStep) => void;
  themeClasses: any;
  isDarkMode: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const steps: { step: UntaggedStep; label: string; enabled: boolean }[] = [
    { step: 1, label: t("specialInspection.untagged.step1"), enabled: true },
    {
      step: 2,
      label: t("specialInspection.untagged.step2"),
      enabled: canOpenStep2,
    },
  ];
  return (
    <ol
      aria-label={t("specialInspection.untagged.stepsLabel")}
      className="flex items-center gap-2"
    >
      {steps.map(({ step, label, enabled }, index) => {
        const active = step === current;
        // Hecho = el paso 1 una vez que el registro está sellado.
        const done = step === 1 && canOpenStep2 && !active;
        const bubble = active
          ? "bg-primary-500 text-white"
          : done
            ? "bg-green-500/20 text-green-400"
            : isDarkMode
              ? "bg-gray-700 text-gray-300"
              : "bg-gray-200 text-gray-600";
        return (
          <li key={step} className="flex items-center gap-2 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => onSelect(step)}
              disabled={!enabled || active}
              aria-current={active ? "step" : undefined}
              title={
                !enabled
                  ? t("specialInspection.untagged.step2Locked")
                  : undefined
              }
              className={`flex items-center gap-2 min-w-0 flex-1 px-3 py-2 rounded-xl border text-left transition-colors ${
                active
                  ? "border-primary-500/50 bg-primary-500/10"
                  : `${themeClasses.cardBorder} ${enabled ? themeClasses.buttonHover : "opacity-50 cursor-not-allowed"}`
              }`}
            >
              <span
                aria-hidden="true"
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold ${bubble}`}
              >
                {done ? <i className="ri-check-line"></i> : step}
              </span>
              <span
                className={`text-xs font-medium truncate ${active ? themeClasses.text : themeClasses.textSecondary}`}
              >
                {label}
              </span>
            </button>
            {index === 0 && (
              <i
                aria-hidden="true"
                className={`ri-arrow-right-s-line flex-shrink-0 ${themeClasses.textSecondary}`}
              ></i>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Las dos preguntas del tag, antes de la unidad. La posición sólo aparece con
 * "Sí, pero no lo lee": si no trae tag no hay parabrisas que revisar.
 */
export function TagSurveyFields({
  survey,
  onChange,
  themeClasses,
  isDarkMode,
  positionOnly = false,
  idPrefix = "untagged",
}: {
  survey: TagSurvey;
  onChange: (survey: TagSurvey) => void;
  // Salida manual de una unidad que ENTRÓ leída por su tag: se sabe que lo
  // trae, así que sólo se pregunta dónde va (ver exitTagSurveyMode).
  positionOnly?: boolean;
  // Ids distintos si el paso 1 y la salida manual llegan a estar a la vez.
  idPrefix?: string;
  themeClasses: any;
  isDarkMode: boolean;
}) {
  const { t } = useTranslation("truckYard");
  // Los mismos estilos que los campos de ManualRegistrationFields, que van debajo.
  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const selectCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {!positionOnly && (
      <div>
        <label htmlFor={`${idPrefix}-tag-presence`} className={labelCls}>
          {t("specialInspection.untagged.tagSurvey.presenceLabel")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <select
          id={`${idPrefix}-tag-presence`}
          value={survey.tag_presence}
          onChange={(e) =>
            onChange({
              ...survey,
              tag_presence: e.target.value as TagSurvey["tag_presence"],
            })
          }
          className={selectCls}
        >
          <option value="">
            {t("specialInspection.untagged.tagSurvey.select")}
          </option>
          {TAG_PRESENCE_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {t(`specialInspection.untagged.tagSurvey.presence.${value}`)}
            </option>
          ))}
        </select>
      </div>
      )}
      {(positionOnly || survey.tag_presence === "unreadable") && (
        <div>
          <label htmlFor={`${idPrefix}-tag-position`} className={labelCls}>
            {t("specialInspection.untagged.tagSurvey.positionLabel")}{" "}
            <span className="text-red-500">*</span>
          </label>
          <select
            id={`${idPrefix}-tag-position`}
            value={survey.tag_position}
            onChange={(e) =>
              onChange({
                ...survey,
                tag_position: e.target.value as TagSurvey["tag_position"],
              })
            }
            className={selectCls}
          >
            <option value="">
              {t("specialInspection.untagged.tagSurvey.select")}
            </option>
            {TAG_POSITION_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {t(`specialInspection.untagged.tagSurvey.position.${value}`)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Paso 1 de una pipa sin tag: decir QUÉ unidad entró con el tag compartido.
 *
 * Antes de la unidad van dos preguntas —¿trae tag? y, si lo trae, dónde va en
 * el parabrisas— y los campos de la unidad no aparecen hasta contestarlas:
 * así no se puede registrar la unidad sin decir por qué la pluma no la leyó.
 *
 * Son los campos de "Nuevo registro manual" de Accesos (el mismo componente),
 * con tres cosas fijas porque acá no se está creando un evento sino
 * completando uno que ya existe: el patio es el de la inspección, el tipo es
 * la entrada, y el tag es el que leyó la pluma. Guardar no crea nada: el
 * backend actualiza el evento de entrada con lo capturado.
 */
export function UntaggedRegistrationStep({
  inspection,
  themeClasses,
  isDarkMode,
  vehicles,
  onClose,
  onSaveRegistration,
  onGoToStep,
}: {
  inspection: YardInspection;
  themeClasses: any;
  isDarkMode: boolean;
  vehicles: TruckYardVehicle[];
  onClose: () => void;
  /** Resuelve `true` si se guardó; el padre avisa del error si no. */
  onSaveRegistration?: (
    data: UpdateYardInspectionRegistrationParams,
  ) => Promise<boolean>;
  onGoToStep: (step: UntaggedStep) => void;
}) {
  const { t } = useTranslation("truckYard");
  const status = deriveInspectionStatus(inspection);
  const registered = untaggedRegistrationComplete(inspection);
  const yardName = modalYardName(inspection.yard_name);

  const groups = useGroupsStore((s) => s.groups);
  useAllGroups();

  const [form, setForm] = useState<ManualRegistrationFormData>(() =>
    registrationFormFromInspection(inspection),
  );
  const [errors, setErrors] = useState<ManualRegistrationErrors>({});
  const [saving, setSaving] = useState(false);
  const [tagSurvey, setTagSurvey] = useState<TagSurvey>(() =>
    tagSurveyFrom(inspection),
  );
  const surveyDone = tagSurveyComplete(tagSurvey);

  // La nota del registro vive en el EVENTO de entrada, no en la inspección
  // (el contrato dice que no pisa las notas de la inspección). Para que volver
  // a corregir la placa no la borre, se lee del evento; si esa lectura falla
  // el formulario abre igual y `notes` sólo viaja si el operador escribe algo
  // (inspectionRegistrationPayload).
  const [notesKnown, setNotesKnown] = useState(false);
  const notesTouched = useRef(false);
  const entryEventId = inspection.access_entry_id;
  useEffect(() => {
    setForm(registrationFormFromInspection(inspection));
    setTagSurvey(tagSurveyFrom(inspection));
    setErrors({});
    setNotesKnown(false);
    notesTouched.current = false;
    if (!entryEventId) return;
    let cancelled = false;
    fetchYardAccessEvent(entryEventId)
      .then((event) => {
        if (cancelled) return;
        setNotesKnown(true);
        // Lo que el operador ya haya escrito mientras cargaba manda.
        if (!notesTouched.current && event?.notes) {
          setForm((prev) => ({ ...prev, notes: event.notes || "" }));
        }
      })
      .catch(() => {
        /* sin la nota anterior: ver arriba */
      });
    return () => {
      cancelled = true;
    };
    // Sólo al cambiar de inspección: guardar devuelve la misma inspección
    // actualizada, y rearmar el formulario ahí pisaría lo que se esté
    // escribiendo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection.id]);

  const unitMissing = isUnitMissing(form);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!onSaveRegistration || !surveyDone) return;
    const found = validateManualRegistration(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      const saved = await onSaveRegistration({
        id: inspection.id,
        ...inspectionRegistrationPayload(form, vehicles, { notesKnown }),
        ...tagSurveyPayload(tagSurvey),
      });
      if (saved) onGoToStep(2);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            <h2 className={`min-w-0 text-base font-bold ${themeClasses.text}`}>
              <span className="block">
                {t("specialInspection.untagged.title")}
              </span>
              {yardName && (
                <span
                  className={`block truncate text-xs font-medium ${themeClasses.textSecondary}`}
                >
                  <span className="sr-only"> · </span>
                  <i aria-hidden="true" className="ri-building-4-line mr-1"></i>
                  {yardName}
                </span>
              )}
            </h2>
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${inspectionStatusClasses(status)}`}
            >
              <i className={`${inspectionStatusIcon(status)} text-xs`}></i>
              {inspectionStatusLabel(status)}
            </span>
            <SpecialTagBadge kind="untagged" className="flex-shrink-0" />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("specialInspection.close")}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors flex-shrink-0`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="p-6 space-y-5">
            <UntaggedStepIndicator
              current={1}
              canOpenStep2={registered}
              onSelect={onGoToStep}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
            />

            <p
              className={`text-xs leading-relaxed ${themeClasses.textSecondary}`}
            >
              <i className="ri-information-line mr-1"></i>
              {registered
                ? t("specialInspection.untagged.introCorrect")
                : t("specialInspection.untagged.intro")}
            </p>

            <TagSurveyFields
              survey={tagSurvey}
              onChange={setTagSurvey}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
            />

            {!surveyDone ? (
              <p className={`text-xs ${themeClasses.textSecondary}`}>
                <i className="ri-arrow-up-line mr-1"></i>
                {t("specialInspection.untagged.tagSurvey.pendingHint")}
              </p>
            ) : (
              <ManualRegistrationFields
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                form={form}
                errors={errors}
                onChange={(patch) => {
                  if ("notes" in patch) notesTouched.current = true;
                  setForm((prev) => ({ ...prev, ...patch }));
                }}
                onClearError={(field) =>
                  setErrors((prev) => ({ ...prev, [field]: undefined }))
                }
                yards={[
                  {
                    id: inspection.yard_id,
                    name: yardName || inspection.yard_id,
                  },
                ]}
                groups={groups}
                vehicles={vehicles}
                lockedYardId={inspection.yard_id}
                yardLockedHint={t("specialInspection.untagged.yardLockedHint")}
                typeLocked
                readOnlyTag={{
                  label: t("specialInspection.untagged.tagLabel"),
                  value: inspection.card_number || "—",
                }}
              />
            )}
          </div>

          <div className="sticky bottom-0 flex items-center justify-end gap-3 flex-wrap px-6 py-4 border-t border-gray-700/30 bg-inherit rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium text-sm transition-all border ${themeClasses.cardBorder}`}
            >
              {t("specialInspection.close")}
            </button>
            {registered && (
              <button
                type="button"
                onClick={() => onGoToStep(2)}
                className={`px-5 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium text-sm transition-all border ${themeClasses.cardBorder}`}
              >
                {t("specialInspection.untagged.skipToInspection")}
                <i className="ri-arrow-right-line ml-1"></i>
              </button>
            )}
            <button
              type="submit"
              disabled={
                saving || !surveyDone || unitMissing || !onSaveRegistration
              }
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1"></i>
                  {t("specialInspection.saving")}
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-1"></i>
                  {registered
                    ? t("specialInspection.untagged.saveCorrection")
                    : t("specialInspection.untagged.saveAndContinue")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
